import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Target,
  CheckCircle2,
  Mail,
  Clock,
  Edit3,
  DollarSign,
  Loader2,
  Eye,
  MousePointer,
  RefreshCw,
  Database,
  AlertTriangle,
  TrendingUp,
  Send,
  ShieldAlert,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES, PIPELINE_STAGES } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { StatCard, Badge } from "../components/ui";
import {
  fetchContacts,
  fetchPipeline,
  fetchNewsletters,
  fetchEngagementStats,
  fetchDashboardStats,
  fetchSyncStatus,
  triggerFullSync,
  fetchNewsletterAnalytics,
  deriveContactType,
  displayName,
  initials,
  fmtDate,
} from "../services/api";

function SyncStatusBadge({ syncData }) {
  const lastSync = syncData.lastIncrementalSync || syncData.lastFullSync;
  let label = "No Syncs";
  let color = colors.textSecondary;
  let bg = "rgba(90,100,120,0.12)";

  if (lastSync) {
    const ageMs = Date.now() - new Date(lastSync).getTime();
    const ageHours = ageMs / (1000 * 60 * 60);
    if (ageHours < 1) {
      label = "Healthy";
      color = colors.success;
      bg = "rgba(16,185,129,0.12)";
    } else if (ageHours < 24) {
      label = "OK";
      color = colors.warning;
      bg = "rgba(245,158,11,0.12)";
    } else {
      label = "Stale";
      color = colors.error;
      bg = "rgba(239,68,68,0.12)";
    }
  }

  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        fontFamily: fonts.body,
        color,
        background: bg,
        padding: "3px 10px",
        borderRadius: 12,
      }}
    >
      {label}
    </span>
  );
}

function SyncMetric({ label, value }) {
  return (
    <div
      style={{
        padding: 12,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: colors.textSecondary,
          fontFamily: fonts.body,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          fontFamily: fonts.mono,
          color: colors.text,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/** Strip merge tags and series prefix from newsletter subject for display. */
function displayTitle(subject) {
  if (!subject) return "Untitled";
  const stripped = subject.replace(/\{\{[^}]+\}\},?\s*/g, "").trim();
  // Only strip "Prefix: " if merge tags were present (series pattern)
  if (stripped !== subject && /^[^:]+:\s+/.test(stripped)) {
    return stripped.replace(/^[^:]+:\s+/, "").trim() || stripped;
  }
  return stripped || subject;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const tenant = useTenant();
  const [contactCount, setContactCount] = useState(0);
  const [recentContacts, setRecentContacts] = useState([]);
  const [pipelineData, setPipelineData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [syncData, setSyncData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opensModal, setOpensModal] = useState(null); // { subject, contacts[], loading }
  const [opensLoading, setOpensLoading] = useState(false);

  const hasGhl = tenant.features?.ghl !== false;

  useEffect(() => {
    const fetches = [
      fetchContacts({ limit: 20 }).catch(() => ({ total: 0, contacts: [] })),
      fetchPipeline({ range: 90 }).catch(() => null),
      fetchNewsletters().catch(() => null),
      fetchEngagementStats().catch(() => null),
      fetchDashboardStats().catch(() => null),
    ];
    if (hasGhl) fetches.push(fetchSyncStatus().catch(() => null));

    Promise.all(fetches)
      .then(([contactsRes, pipeline, campaigns, engagement, dash, sync]) => {
        setContactCount(contactsRes.total || 0);
        const nonTeam = (contactsRes.contacts || []).filter(
          (c) => deriveContactType(c.tags, c.ghlTags) !== "team",
        );
        setRecentContacts(nonTeam.slice(0, 6));
        setPipelineData(pipeline);
        setCampaignData(campaigns);
        setEngagementData(engagement);
        setDashStats(dash);
        if (sync) setSyncData(sync);
      })
      .finally(() => setLoading(false));
  }, []);

  const pStats = pipelineData?.stats || {};
  const cStats = campaignData?.stats || {};
  const eStats = engagementData || {};
  const deals = pipelineData?.deals || [];
  const campaigns = campaignData?.campaigns || [];
  const recentPayments = pipelineData?.recentPayments || [];

  const sym = tenant.currency?.symbol || "$";
  const fmtCurrency = (v) => {
    if (!v) return `${sym}0`;
    if (v >= 1000) return `${sym}${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K`;
    return `${sym}${v.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Loader2
          size={24}
          color={colors.accent}
          className="spin"
        />      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1
          style={{
            fontFamily: fonts.heading,
            fontSize: 28,
            fontWeight: 700,
            color: colors.text,
            marginBottom: 4,
          }}
        >
          Dashboard
        </h1>
        <p
          style={{
            fontFamily: fonts.body,
            fontSize: 14,
            color: colors.textSecondary,
          }}
        >
          {tenant.companyName} — Overview
        </p>
      </div>

      <div
        style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}
      >
        <StatCard
          icon={Users}
          label="Total Contacts"
          value={contactCount.toLocaleString()}
          change="live"
          changeType="up"
          onClick={() => navigate("/contacts")}
        />
        <StatCard
          icon={Target}
          label="Active Pipeline"
          value={pStats.activeCount || 0}
          change={`${pStats.wonCount || 0} closed won`}
          changeType="up"
          onClick={() => navigate("/pipeline")}
        />
        <StatCard
          icon={DollarSign}
          label={`Revenue (${pStats.rangeDays || 90}d)`}
          value={fmtCurrency(pStats.totalRevenue)}
          change={`${pStats.paymentCount || 0} payments`}
          changeType="up"
          onClick={() => navigate("/pipeline")}
        />
        <StatCard
          icon={TrendingUp}
          label="New This Week"
          value={`+${dashStats?.contactGrowth?.thisWeek || 0}`}
          change={
            dashStats?.contactGrowth?.change
              ? `${dashStats.contactGrowth.change > 0 ? "+" : ""}${dashStats.contactGrowth.change}%`
              : ""
          }
          changeType={dashStats?.contactGrowth?.change >= 0 ? "up" : "down"}
          onClick={() => navigate("/contacts")}
        />
      </div>

      {/* Recent Contacts */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 24,
          marginBottom: 32,
        }}
      >
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            marginBottom: 20,
          }}
        >
          <span style={{ cursor: "pointer" }} onClick={() => navigate("/contacts")}>Recent Contacts</span>
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {recentContacts.map((contact) => {
            const type = deriveContactType(contact.tags, contact.ghlTags);
            const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
            return (
              <div
                key={contact.id}
                onClick={() => navigate(`/contacts?contact=${contact.id}`)}
                style={{
                  padding: 16,
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  cursor: "pointer",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.accent;
                  e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.accent}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: typeInfo.bg,
                    color: typeInfo.color,
                    fontFamily: fonts.heading,
                    fontWeight: 700,
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  {initials(contact)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontFamily: fonts.body,
                      color: colors.text,
                      fontWeight: 600,
                    }}
                  >
                    {displayName(contact)}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      marginBottom: 4,
                    }}
                  >
                    {contact.company || contact.email}
                  </div>
                  <Badge color={typeInfo.color} bg={typeInfo.bg}>
                    {typeInfo.label}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Newsletter Performance + Recent Activity */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginBottom: 32,
        }}
      >
        {/* Newsletter Performance */}
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 24,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              marginBottom: 20,
            }}
          >
            <span style={{ cursor: "pointer" }} onClick={() => navigate("/newsletters")}>Newsletter Performance</span>
          </h3>
          {(dashStats?.newsletterPerformance || []).length > 0 ? (
            dashStats.newsletterPerformance.map((nl, i) => (
              <div
                key={nl.id}
                onClick={async () => {
                  setOpensModal({
                    subject: displayTitle(nl.subject),
                    contacts: [],
                    loading: true,
                  });
                  try {
                    const data = await fetchNewsletterAnalytics(nl.id);
                    setOpensModal({
                      subject: displayTitle(nl.subject),
                      contacts: data.topContacts || [],
                      loading: false,
                    });
                  } catch {
                    setOpensModal((prev) =>
                      prev ? { ...prev, loading: false, error: true } : null,
                    );
                  }
                }}
                style={{
                  padding: "10px 0",
                  borderBottom:
                    i < dashStats.newsletterPerformance.length - 1
                      ? `1px solid ${colors.border}`
                      : "none",
                  cursor: "pointer",
                  borderRadius: 4,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = colors.bgActive)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: colors.text,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      marginRight: 12,
                    }}
                  >
                    {displayTitle(nl.subject)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      fontFamily: fonts.mono,
                      flexShrink: 0,
                    }}
                  >
                    {nl.sentAt ? fmtDate(nl.sentAt) : "—"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 11,
                    }}
                  >
                    <Eye size={10} color="#3b82f6" />
                    <span
                      style={{
                        fontFamily: fonts.mono,
                        fontWeight: 700,
                        color: "#3b82f6",
                      }}
                    >
                      {nl.uniqueOpens}
                    </span>
                    <span style={{ color: colors.textSecondary }}>
                      opens ({nl.openRate}%)
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 11,
                    }}
                  >
                    <MousePointer size={10} color={colors.accent} />
                    <span
                      style={{
                        fontFamily: fonts.mono,
                        fontWeight: 700,
                        color: colors.accent,
                      }}
                    >
                      {nl.uniqueClicks}
                    </span>
                    <span style={{ color: colors.textSecondary }}>
                      clicks ({nl.clickRate}%)
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      color: colors.textSecondary,
                      marginLeft: "auto",
                    }}
                  >
                    {nl.totalSent} sent
                  </span>
                </div>
              </div>
            ))
          ) : campaigns.length > 0 ? (
            campaigns.slice(0, 4).map((c) => (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 0",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: colors.text,
                      marginBottom: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.name}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {c.recipients > 0
                      ? `${c.recipients.toLocaleString()} sent`
                      : "No recipients"}{" "}
                    ·{" "}
                    {c.sentAt || c.scheduledAt
                      ? fmtDate(c.sentAt || c.scheduledAt)
                      : "Not scheduled"}
                  </div>
                </div>
                <Badge
                  color={
                    c.status === "completed"
                      ? colors.success
                      : c.status === "scheduled"
                        ? colors.warning
                        : colors.textSecondary
                  }
                  bg={
                    c.status === "completed"
                      ? "rgba(16,185,129,0.12)"
                      : c.status === "scheduled"
                        ? "rgba(245,158,11,0.12)"
                        : "rgba(90,100,120,0.12)"
                  }
                >
                  {c.status === "completed" ? (
                    <CheckCircle2 size={10} />
                  ) : c.status === "scheduled" ? (
                    <Clock size={10} />
                  ) : (
                    <Edit3 size={10} />
                  )}
                  {c.status}
                </Badge>
              </div>
            ))
          ) : (
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                padding: 16,
                textAlign: "center",
              }}
            >
              No newsletters yet
            </div>
          )}
        </div>

        {/* Recent Activity Feed */}
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 24,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              marginBottom: 20,
            }}
          >
            Recent Activity
          </h3>
          {(dashStats?.recentActivity || []).length > 0 ? (
            dashStats.recentActivity.slice(0, 15).map((evt, i) => {
              const iconMap = {
                open: { icon: Eye, color: "#3b82f6" },
                click: { icon: MousePointer, color: colors.accent },
                delivered: { icon: Send, color: colors.success },
                bounce: { icon: AlertTriangle, color: colors.danger },
                spam_report: { icon: ShieldAlert, color: colors.danger },
              };
              const info = iconMap[evt.type] || {
                icon: Mail,
                color: colors.textSecondary,
              };
              const Icon = info.icon;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 0",
                    borderBottom:
                      i < Math.min(dashStats.recentActivity.length, 15) - 1
                        ? `1px solid ${colors.borderLight}`
                        : "none",
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: info.color + "18",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={10} color={info.color} />
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.textSecondary,
                      textTransform: "capitalize",
                      minWidth: 55,
                    }}
                  >
                    {evt.type}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: colors.text,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {evt.email}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: colors.textSecondary,
                      fontFamily: fonts.mono,
                      flexShrink: 0,
                    }}
                  >
                    {evt.timestamp ? fmtDate(evt.timestamp) : ""}
                  </span>
                </div>
              );
            })
          ) : (
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                padding: 16,
                textAlign: "center",
              }}
            >
              No activity yet
            </div>
          )}
        </div>
      </div>

      {/* Opens Detail Modal */}
      {opensModal && (
        <div
          onClick={() => setOpensModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 24,
              width: 520,
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 16,
                  color: colors.text,
                  margin: 0,
                }}
              >
                {opensModal.subject}
              </h3>
              <button
                onClick={() => setOpensModal(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.textSecondary,
                  cursor: "pointer",
                  fontSize: 18,
                  padding: "0 4px",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                marginBottom: 12,
              }}
            >
              {opensModal.loading
                ? "Loading..."
                : opensModal.error
                  ? "Failed to load data"
                  : `${opensModal.contacts.filter((c) => c.opens > 0).length} unique opens`}
            </div>
            {opensModal.loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 32,
                }}
              >
                <Loader2
                  size={20}
                  color={colors.accent}
                  className="spin"
                />
              </div>
            ) : opensModal.error ? (
              <div
                style={{
                  color: colors.danger,
                  fontSize: 12,
                  padding: 16,
                  textAlign: "center",
                }}
              >
                Could not load analytics
              </div>
            ) : (
              <div style={{ overflowY: "auto", flex: 1 }}>
                {opensModal.contacts.filter((c) => c.opens > 0).length === 0 ? (
                  <div
                    style={{
                      color: colors.textSecondary,
                      fontSize: 12,
                      textAlign: "center",
                      padding: 24,
                    }}
                  >
                    No opens recorded yet
                  </div>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr
                        style={{ borderBottom: `2px solid ${colors.border}` }}
                      >
                        <th
                          style={{
                            textAlign: "left",
                            padding: "6px 8px",
                            color: colors.textSecondary,
                            fontWeight: 600,
                          }}
                        >
                          Email
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "6px 8px",
                            color: colors.textSecondary,
                            fontWeight: 600,
                          }}
                        >
                          Opens
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "6px 8px",
                            color: colors.textSecondary,
                            fontWeight: 600,
                          }}
                        >
                          Clicks
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "6px 8px",
                            color: colors.textSecondary,
                            fontWeight: 600,
                          }}
                        >
                          First Opened
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {opensModal.contacts
                        .filter((c) => c.opens > 0)
                        .map((c) => (
                          <tr
                            key={c.email}
                            style={{
                              borderBottom: `1px solid ${colors.borderLight}`,
                            }}
                          >
                            <td
                              style={{ padding: "6px 8px", color: colors.text }}
                            >
                              {c.email}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                textAlign: "center",
                                fontFamily: fonts.mono,
                                fontWeight: 700,
                                color: "#3b82f6",
                              }}
                            >
                              {c.opens}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                textAlign: "center",
                                fontFamily: fonts.mono,
                                fontWeight: 700,
                                color:
                                  c.clicks > 0
                                    ? colors.accent
                                    : colors.textSecondary,
                              }}
                            >
                              {c.clicks}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                textAlign: "right",
                                color: colors.textSecondary,
                                fontFamily: fonts.mono,
                              }}
                            >
                              {c.firstOpen ? fmtDate(c.firstOpen) : "—"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pipeline Overview */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 24,
          marginBottom: 32,
        }}
      >
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            marginBottom: 20,
          }}
        >
          <span style={{ cursor: "pointer" }} onClick={() => navigate("/pipeline")}>Pipeline Overview</span>
        </h3>
        {PIPELINE_STAGES.filter(
          (s) => !["closed_won", "closed_lost"].includes(s.id),
        ).map((stage) => {
          const stageDeals = deals.filter((d) => d.stage === stage.id);
          const value = stageDeals.reduce((s, d) => s + d.value, 0);
          const maxValue = Math.max(
            ...PIPELINE_STAGES.filter(
              (s) => !["closed_won", "closed_lost"].includes(s.id),
            ).map((st) =>
              deals
                .filter((d) => d.stage === st.id)
                .reduce((s, d) => s + d.value, 0),
            ),
            1,
          );
          return (
            <div
              key={stage.id}
              style={{ marginBottom: 16, cursor: "pointer" }}
              onClick={() => navigate("/pipeline")}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontFamily: fonts.body,
                    color: colors.textSecondary,
                  }}
                >
                  {stage.label}{" "}
                  <span style={{ color: colors.textSecondary }}>
                    ({stageDeals.length})
                  </span>
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontFamily: fonts.mono,
                    color: colors.text,
                    fontWeight: 600,
                  }}
                >
                  {fmtCurrency(value)}
                </span>
              </div>
              <div
                style={{
                  height: 6,
                  background: colors.bgActive,
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min((value / maxValue) * 100, 100)}%`,
                    background: stage.color,
                    borderRadius: 3,
                    transition: "width 0.6s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* GHL Sync Health */}
      {hasGhl && syncData && (
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 24,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Database size={18} color={colors.accent} />
              <h3
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 16,
                  color: colors.text,
                  margin: 0,
                }}
              >
                GHL Sync Health
              </h3>
              <SyncStatusBadge syncData={syncData} />
            </div>
            <button
              onClick={async () => {
                setSyncing(true);
                setSyncError(null);
                try {
                  await triggerFullSync();
                  const updated = await fetchSyncStatus();
                  setSyncData(updated);
                } catch (err) {
                  setSyncError(err.message || "Sync failed");
                  try {
                    const updated = await fetchSyncStatus();
                    setSyncData(updated);
                  } catch {
                    /* ignore */
                  }
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 14px",
                background: syncing ? colors.bgActive : colors.accent,
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: syncing ? "default" : "pointer",
                opacity: syncing ? 0.7 : 1,
              }}
            >
              <RefreshCw
                size={13}
                className={syncing ? "spin" : ""}
              />
              {syncing ? "Syncing..." : "Sync Now"}
            </button>
          </div>

          {syncError && (
            <div
              style={{
                padding: "10px 14px",
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 12,
                color: "#ef4444",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AlertTriangle size={14} />
              <span>
                <strong>Sync failed:</strong> {syncError}
              </span>
              <button
                onClick={() => setSyncError(null)}
                style={{
                  marginLeft: "auto",
                  background: "none",
                  border: "none",
                  color: "#ef4444",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: "0 4px",
                }}
              >
                ×
              </button>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <SyncMetric
              label="Total Contacts"
              value={(syncData.totalContacts || 0).toLocaleString()}
            />
            <SyncMetric
              label="Last Full Sync"
              value={
                syncData.lastFullSync ? fmtDate(syncData.lastFullSync) : "Never"
              }
            />
            <SyncMetric
              label="Last Incremental"
              value={
                syncData.lastIncrementalSync
                  ? fmtDate(syncData.lastIncrementalSync)
                  : "Never"
              }
            />
          </div>

          {syncData.recentSyncLogs?.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontFamily: fonts.body,
                  color: colors.textSecondary,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                Recent Sync Activity
              </div>
              {syncData.recentSyncLogs.slice(0, 5).map((log, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    borderBottom:
                      i < Math.min(syncData.recentSyncLogs.length, 5) - 1
                        ? `1px solid ${colors.borderLight}`
                        : "none",
                    fontSize: 12,
                    fontFamily: fonts.body,
                  }}
                >
                  {log.status === "completed" ? (
                    <CheckCircle2 size={13} color={colors.success} />
                  ) : log.status === "failed" ? (
                    <AlertTriangle size={13} color={colors.error} />
                  ) : (
                    <Clock size={13} color={colors.warning} />
                  )}
                  <span style={{ color: colors.textSecondary }}>
                    {log.type === "full_sync"
                      ? "Full"
                      : log.type === "incremental"
                        ? "Incremental"
                        : log.type === "webhook"
                          ? "Webhook"
                          : log.type}
                  </span>
                  <span style={{ color: colors.text }}>
                    {log.recordsProcessed || 0} records
                  </span>
                  {log.errors > 0 && (
                    <span
                      style={{ color: colors.error }}
                      title={log.errorMessages?.join("; ") || ""}
                    >
                      {log.errors} error{log.errors > 1 ? "s" : ""}
                    </span>
                  )}
                  <span
                    style={{
                      marginLeft: "auto",
                      color: colors.textSecondary,
                      fontSize: 11,
                      fontFamily: fonts.mono,
                    }}
                  >
                    {log.completedAt ? fmtDate(log.completedAt) : "—"}
                    {log.duration ? ` (${log.duration})` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
